import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { slides } from './slides';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export function Deck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(prev => {
      const nextIndex = prev + newDirection;
      if (nextIndex < 0) return 0;
      if (nextIndex >= slides.length) return slides.length - 1;
      return nextIndex;
    });
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space' || e.key === 'PageDown') {
        paginate(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        paginate(-1);
      } else if (e.key === 'Home') {
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const CurrentSlide = slides[currentIndex];
  const variants = {
    enter: (direction: number) => {
      if (prefersReducedMotion) return {
        opacity: 0
      };
      return {
        x: direction > 0 ? 100 : -100,
        opacity: 0,
        scale: 0.98
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => {
      if (prefersReducedMotion) return {
        opacity: 0
      };
      return {
        zIndex: 0,
        x: direction < 0 ? 100 : -100,
        opacity: 0,
        scale: 0.98
      };
    }
  };
  return <div className="relative h-screen h-[100dvh] w-screen overflow-hidden bg-background text-foreground flex flex-col" data-id="element-2">
      {/* Global Background Atmosphere */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-3">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-accent/10 blur-[120px]" data-id="element-4" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-accent-secondary/10 blur-[120px]" data-id="element-5" />
      </div>

      {/* Slide Content */}
      <div className="relative z-10 flex-1 min-h-0 w-full" data-id="element-6">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-7">
          <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          },
          scale: {
            duration: 0.3
          }
        }} className="absolute inset-0 w-full h-full overflow-y-auto hide-scrollbar" data-id="element-8">
            <CurrentSlide data-id="element-9" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress, Controls & Chalkline link — in-flow bottom bar so it stays within the viewport */}
      <div className="relative z-20 flex-shrink-0 w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3" data-id="element-10">
        {/* Progress (left) */}
        <div className="flex gap-1.5 sm:gap-2 flex-shrink min-w-0" data-id="element-11">
          {slides.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-6 sm:w-8 bg-accent' : idx < currentIndex ? 'w-1.5 sm:w-2 bg-accent/40' : 'w-1.5 sm:w-2 bg-surface-hover'}`} data-id="element-12" />)}
        </div>

        {/* Powered by Chalkline (subtle, persistent) — hidden on the smallest screens to keep the bar compact */}
        <a href="https://chalkline.com" target="_blank" rel="noopener noreferrer" className="hidden sm:block text-xs text-muted/60 hover:text-accent tracking-wide transition-colors whitespace-nowrap" data-id="element-13">
          Powered by <span className="font-semibold" data-id="element-14">Chalkline</span>
        </a>

        {/* Controls (right) */}
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" data-id="element-15">
          <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="p-2 rounded-full bg-surface hover:bg-surface-hover text-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-surface disabled:hover:text-muted transition-colors" aria-label="Previous slide" data-id="element-16">
            <ChevronLeft size={20} data-id="element-17" />
          </button>
          <div className="text-xs sm:text-sm font-medium text-muted font-display tracking-widest whitespace-nowrap" data-id="element-18">
            {String(currentIndex + 1).padStart(2, '0')} /{' '}
            {String(slides.length).padStart(2, '0')}
          </div>
          <button onClick={() => paginate(1)} disabled={currentIndex === slides.length - 1} className="p-2 rounded-full bg-surface hover:bg-surface-hover text-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-surface disabled:hover:text-muted transition-colors" aria-label="Next slide" data-id="element-19">
            <ChevronRight size={20} data-id="element-20" />
          </button>
        </div>
      </div>
    </div>;
}